import React, { useEffect, useState, useRef } from 'react';
import { Box, Button, Typography, Autocomplete, TextField, IconButton, Tooltip } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {
  initialPharmacyValues,
  validationPharmacySchema,
  useBillingConfigOptions,
  PatientSourceWithTariffs,
  PharmacyFormValues,
  PharmacyItem as FormPharmacyItem,
  useCreatePharmacyInvoice,
  useExistingPatientSelectedPrescriptions,
} from './dispenseHelper';
import ItemSearchField, { PharmacyItem as ApiPharmacyItem, PharmacyItemBatch } from '../Billing/Pharmacy/newPharmacy/ItemSearchField';
import BatchSelectionModal from '../Billing/Pharmacy/newPharmacy/BatchSelectionModal';
import ItemsList from './ItemsList';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { ConfirmationModal } from '../../components/confirmationModal';
import { useAlert } from '../../components/alertProvider';
import { QRHiddenFieldSx } from './pharmacyDispanseHelper';
import { useQRScanHandler } from './qrScanItemMapper';
import {
  mapPrescriptionToFormItem,
  submitPharmacyDispense,
  createFormItemFromBatch,
  createReplacementItem,
} from './directDispenseFormHelpers';
import { useClinic } from '../../components/ClinicProvider';
import { Department, mapUserClinicsResponse, UserClinicDepartments } from '../Inventory/Common/inventory.helper';
import { getPatientDrugs } from '../../components/treatmentPlan/Drugs/DrugsHomeHelper';
import { useLoader } from '../../components/loaderProvider/loader';
import TableLoader from '../../components/common/TableLoader';
import { ReactComponent as ScanIcon } from '../../assets/SVG/scan.svg';
import { useTranslation } from 'react-i18next';

const DirectPharmacyDispenseForm = () => {
  // ─────────────────────────────────────────────────────────────────────────
  // State
  // ─────────────────────────────────────────────────────────────────────────
  const [patientSourceOptions, setPatientSourceOptions] = useState<PatientSourceWithTariffs[]>([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState<PharmacyFormValues | null>(null);
  const [itemReplaceConfirmOpen, setItemReplaceConfirmOpen] = useState(false);
  const [pendingItemReplacement, setPendingItemReplacement] = useState<{
    index: number;
    item: ApiPharmacyItem;
  } | null>(null);
  const [itemReplaceReason, setItemReplaceReason] = useState('');
  const [storeList, setStoreList] = useState<Department[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
  const [prescriptionIds, setPrescriptionIds] = useState<number[]>([]);
  const [hasQtyError, setHasQtyError] = useState(false);
  
  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);
  const [pendingDeleteSetFieldValue, setPendingDeleteSetFieldValue] = useState<((field: string, value: unknown) => void) | null>(null);
  const [pendingDeleteItems, setPendingDeleteItems] = useState<FormPharmacyItem[]>([]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // Hooks & Context
  // ─────────────────────────────────────────────────────────────────────────
  const { addAlert } = useAlert();
  const { t: tBilling } = useTranslation('billing_config');
  const { ClinicId } = useClinic();
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const { id: patientIdParam } = useParams<{ id: string }>();
  const patientId = patientIdParam ?? undefined;

  // ─────────────────────────────────────────────────────────────────────────
  // Refs
  // ─────────────────────────────────────────────────────────────────────────
  const formikRef = useRef<{
    setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
    setFieldTouched: (field: string, touched?: boolean, shouldValidate?: boolean) => void;
    values: PharmacyFormValues;
      } | null>(null);
  const itemSearchFieldRef = useRef<{ clearSearch: () => void } | null>(null);

  // ─────────────────────────────────────────────────────────────────────────
  // Effects - Load stores
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user') || '{}';
      const user = JSON.parse(userStr) as UserClinicDepartments;
      const mapped = mapUserClinicsResponse(user);
      const defaultClinic = mapped.find((c) => c.id === Number(ClinicId));
      if (defaultClinic) {
        const departments = defaultClinic.departments || [];
        setStoreList(departments);
        const defaultDept = departments.find((d) => d.name === 'Pharmacy');
        if (defaultDept) {
          setSelectedStoreId(defaultDept.id);
        }
      }
    } catch {
      setStoreList([]);
    }
  }, [ClinicId]);

  // ─────────────────────────────────────────────────────────────────────────
  // Effects - Fetch prescriptions
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!patientId || Number(patientId) < 1 || !selectedStoreId) return;

    showLoader();
    getPatientDrugs(`?patient=${patientId}&is_dispense=true`)
      .then((res) => {
        const allIds = res.objects.map((item) => item.id);
        setPrescriptionIds(allIds);
      })
      .catch(() => {
        // Silently handle error - user will see empty list
      })
      .finally(() => {
        hideLoader();
      });
  }, [patientId, selectedStoreId, showLoader, hideLoader]);

  // ─────────────────────────────────────────────────────────────────────────
  // API Queries
  // ─────────────────────────────────────────────────────────────────────────
  const {
    data: billingConfigData,
    isLoading: isConfigLoading,
    isError: isConfigError,
    error: configError,
  } = useBillingConfigOptions(patientId);

  const {
    data: selectedPrescriptionsData,
    isLoading: isSelectedPrescriptionsLoading,
  } = useExistingPatientSelectedPrescriptions(
    prescriptionIds.length > 0 ? prescriptionIds : undefined,
    selectedStoreId
  );

  const createPharmacyInvoiceMutation = useCreatePharmacyInvoice();

  // ─────────────────────────────────────────────────────────────────────────
  // Effects - Initialize dropdown options
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!billingConfigData) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { tariff_source_group } = billingConfigData as any;
    if (tariff_source_group?.length) {
      setPatientSourceOptions(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tariff_source_group.map((item: any) => ({
          label: item.label ?? item.name,
          value: item.value ?? item.code,
          tariffs: item.tariffs ?? [],
        }))
      );
    }
  }, [billingConfigData]);

  // Auto-select first patient source
  useEffect(() => {
    if (!formikRef.current) return;
    if (!formikRef.current.values.patientSource && patientSourceOptions.length > 0) {
      const firstSource = patientSourceOptions[0];
      formikRef.current.setFieldValue('patientSource', firstSource.value, false);
      formikRef.current.setFieldTouched('patientSource', true, false);
    }
  }, [patientSourceOptions]);

  // Auto-select first tariff when patient source changes
  useEffect(() => {
    if (!formikRef.current) return;
    const currentSource = formikRef.current.values.patientSource;
    const selectedSource = patientSourceOptions.find(
      (src) => src.value === currentSource
    );
    const tariffOptions = selectedSource?.tariffs || [];
    
    if (currentSource && !formikRef.current.values.tariff && tariffOptions.length > 0) {
      formikRef.current.setFieldValue('tariff', tariffOptions[0].tariff_code, false);
      formikRef.current.setFieldTouched('tariff', true, false);
    }
  }, [formikRef.current?.values.patientSource, patientSourceOptions]);

  // Auto-populate items from selected prescriptions
  useEffect(() => {
    if (!formikRef.current || !selectedPrescriptionsData || isSelectedPrescriptionsLoading) return;
    
    const currentPrescriptions = selectedPrescriptionsData?.items || [];
    
    if (currentPrescriptions.length > 0) {
      const mappedItems = currentPrescriptions.map(mapPrescriptionToFormItem);
      formikRef.current.setFieldValue('items', mappedItems, false);
      
      // Auto-select package if any item has package_details
      const itemWithPackage = currentPrescriptions.find(p => p.package_details && p.package_details.length > 0);
      if (itemWithPackage?.package_details?.[0]?.package_id && !formikRef.current.values.packageId) {
        const packageId = String(itemWithPackage.package_details[0].package_id);
        formikRef.current.setFieldValue('packageId', packageId, false);
      }
    }
  }, [selectedPrescriptionsData, isSelectedPrescriptionsLoading]);

  // ─────────────────────────────────────────────────────────────────────────
  // Submit Handler
  // ─────────────────────────────────────────────────────────────────────────
  const handleSubmitForm = (formValues: PharmacyFormValues) => {
    if (!patientId) {
      addAlert(tBilling('pharmacy_dispense.errors.missing_required_id', { defaultValue: 'Missing required ID: patientId={{patientId}}', patientId }), 'error');
      return;
    }

    submitPharmacyDispense({
      formValues,
      patientId,
      patientSourceOptions,
      selectedStoreId,
      createInvoiceMutation: createPharmacyInvoiceMutation,
      onSuccess: () => {
        addAlert(tBilling('pharmacy_dispense.messages.created_success', { defaultValue: 'Pharmacy dispense created successfully' }), 'success');
        navigate(`/admin/billing/invoice/patient-invoice/${patientId}`);
      },
      onError: (message) => addAlert(message, 'error'),
      onWarning: (message) => addAlert(message, 'warning'),
    });
  };

  const handleConfirmInvoice = () => {
    if (pendingFormValues) {
      setConfirmDialogOpen(false);
      handleSubmitForm(pendingFormValues);
      setPendingFormValues(null);
    }
  };

  // Delete confirmation handlers
  const handleDeleteItemClick = (
    idx: number,
    setFieldValue: (field: string, value: unknown) => void,
    items: FormPharmacyItem[]
  ) => {
    setPendingDeleteIndex(idx);
    setPendingDeleteSetFieldValue(() => setFieldValue);
    setPendingDeleteItems(items);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDeleteItem = () => {
    if (pendingDeleteIndex !== null && pendingDeleteSetFieldValue && pendingDeleteItems.length > 0) {
      const updated = pendingDeleteItems.filter((_, i) => i !== pendingDeleteIndex);
      pendingDeleteSetFieldValue('items', updated);
    }
    setDeleteConfirmOpen(false);
    setPendingDeleteIndex(null);
    setPendingDeleteSetFieldValue(null);
    setPendingDeleteItems([]);
  };

  const handleCancelDeleteItem = () => {
    setDeleteConfirmOpen(false);
    setPendingDeleteIndex(null);
    setPendingDeleteSetFieldValue(null);
    setPendingDeleteItems([]);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Batch Selection Modal State
  // ─────────────────────────────────────────────────────────────────────────
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [selectedItemForBatch, setSelectedItemForBatch] = useState<ApiPharmacyItem | null>(null);
  const [pendingSetFieldValue, setPendingSetFieldValue] = useState<((field: string, value: unknown) => void) | null>(null);
  const [pendingCurrentItems, setPendingCurrentItems] = useState<FormPharmacyItem[]>([]);

  // ─────────────────────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────────────────────
  const handleSelectItemFromSearch = (
    apiItem: ApiPharmacyItem,
    setFieldValue: (field: string, value: unknown) => void,
    currentItems: FormPharmacyItem[]
  ) => {
  // Store the item and open batch selection modal
    setSelectedItemForBatch(apiItem);
    setPendingSetFieldValue(() => setFieldValue);
    setPendingCurrentItems(currentItems);
    setBatchModalOpen(true);
  };

  /**
   * QR Scanner Hook - handles scan mode, input, and success/error callbacks
   */
  const {
    isScanModeActive,
    scanInputRef,
    scanInputValue,
    setScanInputValue,
    handleScanSubmit,
    handleScanModeToggle,
  } = useQRScanHandler({
    formikRef,
    clinicId: Number(ClinicId),
    storeId: selectedStoreId,
    addAlert,
    showLoader,
    hideLoader,
  });

  const handleBatchSelect = (batch: PharmacyItemBatch) => {
    if (!selectedItemForBatch || !pendingSetFieldValue) return;

    const existingItemInList = pendingCurrentItems.find(
      (item) => item.itemCode === selectedItemForBatch.item_code
    );

    const newItem = createFormItemFromBatch({
      batch,
      apiItem: selectedItemForBatch,
      existingItemInList,
    });

    pendingSetFieldValue('items', [...pendingCurrentItems, newItem]);
    setBatchModalOpen(false);
    setSelectedItemForBatch(null);
    setPendingSetFieldValue(null);
    setPendingCurrentItems([]);
    
    if (itemSearchFieldRef.current) {
      itemSearchFieldRef.current.clearSearch();
    }
  };

  const handleBatchModalClose = () => {
    setBatchModalOpen(false);
    setSelectedItemForBatch(null);
    setPendingSetFieldValue(null);
    setPendingCurrentItems([]);
    if (itemSearchFieldRef.current) {
      itemSearchFieldRef.current.clearSearch();
    }
  };

  const handleItemReplaceInternal = (
    index: number,
    apiItem: ApiPharmacyItem,
    setFieldValue: (field: string, value: unknown) => void,
    currentItems: FormPharmacyItem[]
  ) => {
    const originalItem = currentItems[index];
    const replacementItem = createReplacementItem({
      apiItem,
      originalItem,
    });

    const updatedItems = [...currentItems];
    updatedItems[index] = replacementItem;
    setFieldValue('items', updatedItems);
  };

  const handleItemReplaceConfirm = () => {
    if (!pendingItemReplacement || !formikRef.current || !itemReplaceReason.trim()) return;

    const { item } = pendingItemReplacement;
    
    handleItemReplaceInternal(
      pendingItemReplacement.index,
      item,
      formikRef.current.setFieldValue,
      formikRef.current.values.items
    );

    setItemReplaceConfirmOpen(false);
    setItemReplaceReason('');
    setPendingItemReplacement(null);
  };

  const handleItemReplace = (index: number, apiItem: ApiPharmacyItem) => {
    setPendingItemReplacement({ index, item: apiItem });
    setItemReplaceConfirmOpen(true);
    setItemReplaceReason('');
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Hidden QR Scanner Input - captures barcode scanner input */}
      <TextField
        inputRef={scanInputRef}
        value={scanInputValue}
        onChange={(e) => setScanInputValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleScanSubmit();
          }
        }}
        sx={QRHiddenFieldSx}
        aria-hidden="true"
        tabIndex={-1}
      />

      <Formik<PharmacyFormValues>
        initialValues={initialPharmacyValues}
        validationSchema={validationPharmacySchema}
        enableReinitialize
        validateOnBlur={true}
        validateOnChange={false}
        onSubmit={(values, helpers) => {
          handleSubmitForm(values);
          helpers.setSubmitting(false);
        }}
      >
        {({ values, setFieldValue, setFieldTouched, setTouched, handleSubmit, isSubmitting, errors, touched, validateForm }) => {
          formikRef.current = { setFieldValue, setFieldTouched, values };

          return (
            <form onSubmit={handleSubmit}>
              {isConfigLoading && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {tBilling('pharmacy_dispense.loading_billing_configuration', { defaultValue: 'Loading billing configuration...' })}
                </Typography>
              )}
              {isConfigError && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  {tBilling('pharmacy_dispense.error_loading_config', {
                    defaultValue: 'Error loading config: {{error}}',
                    error: configError?.message,
                  })}
                </Typography>
              )}

              <Box sx={{ mt: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Grid2 container spacing={2} sx={{ mb: 3 }} alignItems="center">
                    <Grid2 size={{ xs: 12, md: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {tBilling('pharmacy_dispense.drugs_list_count', { defaultValue: 'Drugs List ({{count}})', count: values.items.length })}
                      </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 8 }} display={'flex'} justifyContent={'space-between'} alignItems={'center'} gap={2}>
                      <Grid2 size={{ xs: 12, md: 6 }} >

                        <Autocomplete
                          options={storeList}
                          value={storeList.find(opt => opt.id === selectedStoreId) || null}
                          onChange={(_, newValue) => {
                            setSelectedStoreId(newValue ? newValue.id : null);
                          }}
                          getOptionLabel={(option: Department) => option.name_label ?? ''}
                          noOptionsText={tBilling('pharmacy_dispense.no_stores_available', { defaultValue: 'No stores available' })}
                          disabled={isScanModeActive}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              name="store"
                              label={tBilling('pharmacy_dispense.store', { defaultValue: 'Store' })}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, md: 6 }} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <ItemSearchField
                            ref={itemSearchFieldRef}
                            onSelectItem={(item) => handleSelectItemFromSearch(item, setFieldValue, values.items)}
                            clinicId={Number(ClinicId)}
                            deptId={selectedStoreId}
                            disabled={isScanModeActive}
                            patientId={patientId ? Number(patientId) : undefined}
                          />
                        </Box>
                        <Tooltip title={tBilling('pharmacy_dispense.scan_qr_to_add_item', { defaultValue: 'Scan QR code to add item' })}>
                          <IconButton
                            size="small"
                            onClick={handleScanModeToggle}
                            sx={{
                              border: '1px solid',
                              borderColor: isScanModeActive ? 'primary.main' : 'divider',
                              borderRadius: 2,
                              p: 1,
                              position: 'relative',
                              bgcolor: isScanModeActive ? 'action.selected' : 'transparent',
                              '&::after': isScanModeActive ? {
                                content: '""',
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'warning.main',
                              } : {},
                            }}
                          >
                            <ScanIcon width={20} height={20} />
                          </IconButton>
                        </Tooltip>
                      </Grid2>
                    </Grid2>
                  </Grid2>
                </Box>

                <Box sx={{ mt: 3, display: 'grid' }}>
                  
                  {isSelectedPrescriptionsLoading ? (
                    <TableLoader minHeight={120} size={32} thickness={3} message={tBilling('pharmacy_dispense.loading_items', { defaultValue: 'Loading items...' })} />
                  ) : (
                    <ItemsList
                      items={values.items}
                      onSelectChange={(idx: number) => {
                        const updated = [...values.items];
                        updated[idx].checked = !updated[idx].checked;
                        setFieldValue('items', updated);
                        if (!updated[idx].checked) {
                          setFieldTouched(`items[${idx}].batchCode`, false);
                          setFieldTouched(`items[${idx}].billedQty`, false);
                          setFieldTouched(`items[${idx}].billedUOM`, false);
                        }
                      }}
                      onSelectAll={(selectAll: boolean) => {
                        const updated = values.items.map(item => ({ ...item, checked: selectAll }));
                        setFieldValue('items', updated);
                      }}
                      onRemoveItem={(idx: number) => {
                        handleDeleteItemClick(idx, setFieldValue, values.items);
                      }}
                      onFieldChange={(idx: number, field: keyof FormPharmacyItem, value: string | number) => {
                        const updated = [...values.items];
                        updated[idx] = { ...updated[idx], [field]: value };
                        setFieldValue('items', updated);
                        if (field === 'batchCode' || field === 'billedQty' || field === 'billedUOM') {
                          setFieldTouched(`items[${idx}].${field}`, false);
                        }
                      }}
                      onItemUpdate={(idx: number, updates: Partial<FormPharmacyItem>) => {
                        const updated = [...values.items];
                        // Apply updates directly - PharmacyItemsTable provides all calculated values
                        updated[idx] = { ...updated[idx], ...updates };
                        setFieldValue('items', updated);
                        if (updates.batchCode !== undefined) {
                          setFieldTouched(`items[${idx}].batchCode`, false);
                        }
                        if (updates.billedQty !== undefined) {
                          setFieldTouched(`items[${idx}].billedQty`, false);
                        }
                        if (updates.billedUOM !== undefined) {
                          setFieldTouched(`items[${idx}].billedUOM`, false);
                        }
                      }}
                      errors={Array.isArray(errors.items) ? errors.items as Array<Record<string, string>> : []}
                      touched={Array.isArray(touched.items) ? touched.items as Array<Record<string, boolean>> : []}
                      onQtyErrorChange={setHasQtyError}
                      onItemReplace={(idx: number, item: ApiPharmacyItem) => {
                        handleItemReplace(idx, item);
                      }}
                      clinicId={Number(ClinicId)}
                      deptId={selectedStoreId}
                      patientId={patientId ? Number(patientId) : undefined}
                      disableBatchSelection={isScanModeActive}
                      onDuplicateItem={(idx: number) => {
                        const itemToDuplicate = values.items[idx];
                        // Create a duplicate with cleared batch and qty
                        const duplicatedItem: FormPharmacyItem = {
                          ...itemToDuplicate,
                          batchCode: '',
                          batchId: undefined,
                          billedQty: 0,
                          totalMRPAmount: 0,
                          concessionAmount: 0,
                          netAmount: 0,
                          checked: true,
                          // Keep availableBatches so user can select a different one
                        };
                        const updated = [...values.items];
                        // Insert duplicate right after the original
                        updated.splice(idx + 1, 0, duplicatedItem);
                        setFieldValue('items', updated);
                      }}
                    />
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => navigate(-1)}
                >
                  {tBilling('pharmacy_dispense.back', { defaultValue: 'Back' })}
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || createPharmacyInvoiceMutation.isPending || hasQtyError}
                  sx={{ fontWeight: 700 }}
                  onClick={async () => {
                    const validationErrors = await validateForm();
                    const isValid = Object.keys(validationErrors).length === 0;
                    if (!isValid) {
                      setTouched({
                        patientSource: true,
                        tariff: true,
                        items: values.items.map((item) =>
                          item.checked ? {
                            batchCode: true,
                            billedQty: true,
                            billedUOM: true,
                            prescribedQty: true,
                            prescribedUOM: true,
                          } : {}
                        ),
                      });
                      return;
                    }
                    const selectedFormItems = values.items.filter((item) => item.checked);
                    if (selectedFormItems.length === 0) {
                      addAlert(tBilling('pharmacy_dispense.warnings.select_at_least_one_item', { defaultValue: 'Please select at least one item to create invoice' }), 'warning');
                      return;
                    }
                    const invalidItems = selectedFormItems.filter(
                      (item) => !item.batchCode || !item.billedQty || !item.billedUOM
                    );
                    if (invalidItems.length > 0) {
                      const touchedItems = values.items.map((item) => {
                        const isInvalid = selectedFormItems.includes(item) && (!item.batchCode || !item.billedQty || !item.billedUOM);
                        return isInvalid ? {
                          batchCode: true,
                          billedQty: true,
                          billedUOM: true,
                        } : {};
                      });
                      setTouched({
                        ...touched,
                        items: touchedItems,
                      });
                      addAlert(tBilling('pharmacy_dispense.warnings.select_batch_qty_uom', { defaultValue: 'Please select batch code, quantity, and UOM for all selected items' }), 'warning');
                      return;
                    }
                    setPendingFormValues(values);
                    setConfirmDialogOpen(true);
                  }}
                >
                  {createPharmacyInvoiceMutation.isPending
                    ? tBilling('pharmacy_dispense.processing', { defaultValue: 'Processing...' })
                    : tBilling('pharmacy_dispense.dispense', { defaultValue: 'Dispense' })}
                </Button>
              </Box>
            </form>
          );
        }}
      </Formik>

      {selectedItemForBatch && (
        <BatchSelectionModal
          open={batchModalOpen}
          onClose={handleBatchModalClose}
          onConfirm={handleBatchSelect}
          itemName={selectedItemForBatch.item_name}
          itemCode={selectedItemForBatch.item_code}
          batches={selectedItemForBatch.batches}
          existingItems={formikRef.current?.values.items.map(item => ({
            itemCode: item.itemCode,
            batchCode: item.batchCode,
          })) || []}
        />
      )}

      <ConfirmationModal
        open={confirmDialogOpen}
        handleClose={() => {
          setConfirmDialogOpen(false);
          setPendingFormValues(null);
        }}
        onConfirm={handleConfirmInvoice}
        type="save"
        title={tBilling('pharmacy_dispense.confirm_pharmacy_dispense_title', { defaultValue: 'Confirm Pharmacy Dispense' })}
        contentMessage={tBilling('pharmacy_dispense.confirm_pharmacy_dispense_message', { defaultValue: 'Are you sure you want to add this pharmacy dispense?' })}
      />

      <ConfirmationModal
        open={itemReplaceConfirmOpen}
        handleClose={() => {
          setItemReplaceConfirmOpen(false);
          setPendingItemReplacement(null);
          setItemReplaceReason('');
        }}
        onConfirm={handleItemReplaceConfirm}
        type="warning"
        title={tBilling('pharmacy_dispense.confirm_item_replacement_title', { defaultValue: 'Confirm Item Replacement' })}
        customContent={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>
              {tBilling('pharmacy_dispense.confirm_change_existing_drug', { defaultValue: 'Do you want to change the existing prescribed drug?' })}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder={tBilling('pharmacy_dispense.enter_item_replacement_reason', { defaultValue: 'Enter the reason for item replacement' })}
              value={itemReplaceReason}
              onChange={(e) => setItemReplaceReason(e.target.value)}
              variant="outlined"
              size="small"
            />
          </Box>
        }
        confirmButtonProps={{
          disabled: !itemReplaceReason.trim(),
        }}
      />

      <ConfirmationModal
        open={deleteConfirmOpen}
        handleClose={handleCancelDeleteItem}
        onConfirm={handleConfirmDeleteItem}
        type="delete"
        title={tBilling('pharmacy_dispense.delete_confirmation', { defaultValue: 'Delete Confirmation' })}
        contentMessage={tBilling('pharmacy_dispense.confirm_delete_item', { defaultValue: 'Are you sure you want to delete this item?' })}
      />
    </>
  );
};

export default DirectPharmacyDispenseForm;
